import React, { useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { Card, Table } from 'react-bootstrap';
import { useEffectOnce } from 'usehooks-ts';
import { isNull, isUndefined, map } from 'lodash';

import { useRouteParam } from '@hooks/params';
import { Loading, LoadingError, RowItem, RowItemProps } from '@components/Base';
import { dUserName } from '@models/User';
import { formatDateTime } from '@utils/datetime';
import { amountToUsd, formatPercentage } from '@utils/currency';

import { selectPolicyCommissionInvoiceDetailsPageReducer } from './selector';
import {
  policyCommissionInvoiceFetch,
  PolicyCommissionInvoiceFetchPayload,
  stateClear,
} from './action';

const ROW_ITEM_PROPS: RowItemProps = {
  className: 'mb-2',
  labelProps: { xs: 6, sm: 6, md: 5 },
};

const PolicyCommissionInvoiceDetailsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const policyCommissionInvoiceId = Number(useRouteParam('policyCommissionInvoiceId'));
  const { invoice } = useSelector(selectPolicyCommissionInvoiceDetailsPageReducer);

  useEffect(() => {
    const payload: PolicyCommissionInvoiceFetchPayload = { id: policyCommissionInvoiceId };
    dispatch(policyCommissionInvoiceFetch(payload));
  }, [policyCommissionInvoiceId]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  if (isUndefined(invoice)) return <Loading />;
  if (isNull(invoice)) return <LoadingError />;

  const {
    createdAt,
    cashFlow,
    insurerPaid,
    frontlinePaid,
    totalPremium,
    brokeragePercentage,
    brokerageAmount,
    totalFrontlineBrokeragePercentage,
    totalFrontlineBrokerageAmount,
    frontlineCommissions,
  } = invoice;

  return (
    <>
      <section>
        <h5 className="mb-4">Commission</h5>
        <Card className="mb-4">
          <Card.Body>
            <RowItem {...ROW_ITEM_PROPS} label={t('common:createdDate')} value={formatDateTime(createdAt)} />
            <RowItem {...ROW_ITEM_PROPS} label="Cash flow" value={cashFlow} />
            <RowItem {...ROW_ITEM_PROPS} label="Insurer payment status" value={insurerPaid ? "Paid" : "Unpaid"} />
            <RowItem {...ROW_ITEM_PROPS} label="Frontline payment status" value={frontlinePaid ? "Paid" : "Unpaid"} />
            <RowItem {...ROW_ITEM_PROPS} label="Total premium" value={amountToUsd(totalPremium)} />
            <RowItem {...ROW_ITEM_PROPS} label="Brokerage percentage" value={formatPercentage(brokeragePercentage)} />
            <RowItem {...ROW_ITEM_PROPS} label="Brokerage amount" value={amountToUsd(brokerageAmount)} />
            <RowItem {...ROW_ITEM_PROPS} label="Total frontline brokerage percentage" value={formatPercentage(totalFrontlineBrokeragePercentage)} />
            <RowItem {...ROW_ITEM_PROPS} label="Total frontline brokerage amount" value={amountToUsd(totalFrontlineBrokerageAmount)} />
            <Table>
              <thead>
                <tr>
                  <th scope="col">Frontline</th>
                  <th scope="col">Brokerage Percentage</th>
                  <th scope="col">Brokerage Amount</th>
                </tr>
              </thead>
              <tbody>
                {map(frontlineCommissions, ({ id, frontline, brokeragePercentage: percentage, brokerageAmount: amount }) => (
                  <tr key={id}>
                    <td>{dUserName(frontline)}</td>
                    <td>{formatPercentage(percentage)}</td>
                    <td>{amountToUsd(amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </section>
    </>
  );
};

export { PolicyCommissionInvoiceDetailsPage };
